import { STORAGE_KEYS } from '@ophelos/shared/constants/storage-keys';
import { requireBoundConnectOrigin } from '@/lib/global/cell-bind';
import { isGlobalSurface } from '@/lib/surface-mode';

export class ConnectApiError extends Error {
  constructor(
    message: string,
    public status?: number,
    public details?: unknown,
  ) {
    super(message);
    this.name = 'ConnectApiError';
  }
}

export type ConnectAuthStage = 'anonymous' | 'limited' | 'full' | null;

async function getConnectApiHeaders(
  authStage: ConnectAuthStage,
  getAccessToken?: () => Promise<string | null>,
): Promise<Record<string, string>> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };

  if (authStage === 'anonymous') {
    return headers;
  }

  if (authStage === 'full' && getAccessToken) {
    try {
      const token = await getAccessToken();
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
        return headers;
      }
    } catch (error) {
      console.error('[connect-api] Error getting access token:', error);
      throw new ConnectApiError('Failed to get access token', 401, error);
    }
  }

  if (authStage === 'limited') {
    const accessCode = localStorage.getItem(STORAGE_KEYS.ACCESS_CODE);
    if (accessCode) {
      headers['X-Access-Code'] = accessCode;
      return headers;
    }
  }

  // Intentionally mirrors core-api.ts: throw when auth is not resolvable (authStage null or no credentials found).
  console.error('[connect-api] No authentication method available. authStage:', authStage);
  throw new ConnectApiError('No authentication found. Please log in again.', 401);
}

async function throwConnectApiError(response: Response): Promise<never> {
  const errorData = await response.json().catch(() => null);
  // Connect API error envelope: { error: { type, code, message }, request_id }.
  const envelope = errorData as { error?: { message?: string } } | null;
  const message = envelope?.error?.message || `Connect API error: ${response.status}`;
  throw new ConnectApiError(message, response.status, errorData);
}

export function getConnectApiBaseUrl(): string {
  if (isGlobalSurface()) return requireBoundConnectOrigin();

  const hostname = typeof window !== 'undefined' ? window.location.hostname : '';
  const envUrl = import.meta.env.VITE_CONNECT_API_URL as string | undefined;

  if (hostname === 'connect.localhost' || hostname === 'connect.test') {
    return '';
  }
  if (envUrl) {
    return envUrl;
  }
  return 'http://localhost:8000';
}

async function connectRequest<T>(
  path: string,
  init: { method: 'GET' | 'POST' | 'PUT'; body?: unknown },
  authStage: ConnectAuthStage,
  getAccessToken?: () => Promise<string | null>,
): Promise<T> {
  const headers = await getConnectApiHeaders(authStage, getAccessToken);
  const response = await fetch(`${getConnectApiBaseUrl()}${path}`, {
    method: init.method,
    headers,
    body: init.body === undefined ? undefined : JSON.stringify(init.body),
  });

  if (!response.ok) {
    return throwConnectApiError(response);
  }

  // 200 with a null JSON body is a valid response for these endpoints.
  return (await response.json()) as T;
}

export interface ActiveDiscountOfferDto {
  offer_id: string;
  campaign_id: string;
  case_id: string;
  status: string;
  discount_config_snapshot: unknown;
  expires_at: string | null;
}

export async function getActiveDiscountOfferFromConnect(
  caseId: string,
  authStage: 'limited' | 'full' | null,
  getAccessToken?: () => Promise<string | null>,
): Promise<ActiveDiscountOfferDto | null> {
  return connectRequest<ActiveDiscountOfferDto | null>(
    `/connect/v1/cases/${caseId}/active-discount-offer`,
    { method: 'GET' },
    authStage,
    getAccessToken,
  );
}

/** The account's livechat request that is with the support team: the customer's message and
 *  when they sent it, both null when the request names no message. */
export interface OpenHandoffDto {
  message: string | null;
  sent_at: string | null;
}

/** Null when the account has no request with the team. */
export async function getOpenHandoffFromConnect(
  authStage: ConnectAuthStage,
  getAccessToken?: () => Promise<string | null>,
): Promise<OpenHandoffDto | null> {
  return connectRequest<OpenHandoffDto | null>('/connect/v1/livechat/open-handoff', { method: 'GET' }, authStage, getAccessToken);
}

export interface CompiledConfigDto {
  domain: string;
  config_json: Record<string, unknown>;
  compiled_at: string | null;
}

export interface AccountDataDto {
  account_id: string;
  account_external_ref: string | null;
  account_type: string | null;
  account_created_on: string | null;
  account_preferred_language: string | null;
  customer_id: string;
  customer_given_name: string | null;
  customer_family_name: string | null;
  customer_type: string | null;
  customer_status: string | null;
  customer_email: string | null;
  customer_phone: string | null;
}

export async function getAccountDataFromConnect(
  accountId: string,
  authStage: ConnectAuthStage,
  getAccessToken?: () => Promise<string | null>,
): Promise<AccountDataDto | null> {
  return connectRequest<AccountDataDto | null>(
    `/connect/v1/accounts/${accountId}`, { method: 'GET' }, authStage, getAccessToken,
  );
}

export async function getVerificationContactsFromConnect(): Promise<{
  email: string | null;
  phone: string | null;
}> {
  const body = await connectRequest<{ email: string | null; phone: string | null }>(
    '/connect/v1/auth/verification-contacts', { method: 'GET' }, 'limited',
  );
  return { email: body?.email ?? null, phone: body?.phone ?? null };
}

/**
 * Masked *contactable* email for the account's customer (active, non-DNC, in-window),
 * or null when none is usable. This is the payer-email presence signal for checkout —
 * distinct from `getVerificationContactsFromConnect`, which intentionally surfaces any
 * email (incl. DNC) for identity verification. Using the contactable predicate here keeps
 * "prompt for an email" in step with the server's checkout resolution.
 */
export async function getPayerEmailFromConnect(
  accountId: string,
  authStage: ConnectAuthStage,
  getAccessToken?: () => Promise<string | null>,
): Promise<{ email: string | null }> {
  const body = await connectRequest<{ email: string | null }>(
    `/connect/v1/contacts/payer-email?account_id=${encodeURIComponent(accountId)}`,
    { method: 'GET' },
    authStage,
    getAccessToken,
  );
  return { email: body?.email ?? null };
}

/**
 * Persist a customer-supplied email when they have none usable, ahead of checkout.
 * No-op server-side if a contactable email already exists (it is never overwritten).
 */
export async function ensureCustomerEmailOnConnect(
  accountId: string,
  email: string,
  authStage: ConnectAuthStage,
  getAccessToken?: () => Promise<string | null>,
): Promise<void> {
  await connectRequest(
    '/connect/v1/contacts/actions/ensure-email',
    { method: 'POST', body: { account_id: accountId, email } },
    authStage,
    getAccessToken,
  );
}

export async function sendMagicAuthCodeOnConnect(): Promise<void> {
  await connectRequest('/connect/v1/auth/actions/send-magic-auth-code', { method: 'POST' }, 'limited');
}

export async function claimCustomerAccountOnConnect(
  accessCode: string,
  accessToken: string,
): Promise<{ customer_id: string; account_id: string }> {
  return connectRequest(
    '/connect/v1/auth/actions/claim-account',
    { method: 'POST', body: { access_code: accessCode } },
    'full',
    async () => accessToken,
  );
}

export async function logPortalActivityToConnect(
  accountId: string,
  eventType: 'login' | 'payment_started' | 'payment_completed' | 'dispute_started',
  metadata: Record<string, unknown>,
  authStage: ConnectAuthStage,
  getAccessToken?: () => Promise<string | null>,
): Promise<void> {
  await connectRequest(
    '/connect/v1/activity/actions/log',
    { method: 'POST', body: { account_id: accountId, event_type: eventType, metadata } },
    authStage,
    getAccessToken,
  );
}

export interface PaymentSessionDto {
  id: string;
  case_id: string;
  client_id: string;
  payment_type: string;
  amount_subunits: number | null;
  status: string;
  created_at: string | null;
  updated_at: string | null;
}

export interface PaymentDistributionPreviewDto {
  distribution: Array<{ case_id: string; amount_subunits: number }>;
  total_subunits: number;
}

/** Ledger-accurate preview of how an account payment splits across its cases.
 * Read-only — mints no session/plan. The backend allocator owns the split
 * strategy (FIFO today); callers must not re-derive it. */
export async function previewDistributionOnConnect(
  input: { accountId: string; amountSubunits: number; currency: string },
  authStage: ConnectAuthStage,
  getAccessToken?: () => Promise<string | null>,
): Promise<PaymentDistributionPreviewDto> {
  return connectRequest<PaymentDistributionPreviewDto>(
    '/connect/v1/payments/actions/preview-distribution',
    {
      method: 'POST',
      body: {
        account_id: input.accountId,
        amount_subunits: input.amountSubunits,
        currency: input.currency,
      },
    },
    authStage,
    getAccessToken,
  );
}

export async function listPaymentSessionsFromConnect(
  filters: { id?: string; caseId?: string; statuses?: string[]; paymentType?: string },
  authStage: ConnectAuthStage,
  getAccessToken?: () => Promise<string | null>,
): Promise<PaymentSessionDto[]> {
  const params = new URLSearchParams();
  if (filters.id) params.set('id', filters.id);
  if (filters.caseId) params.set('case_id', filters.caseId);
  for (const s of filters.statuses ?? []) params.append('status', s);
  if (filters.paymentType) params.set('payment_type', filters.paymentType);
  const result = await connectRequest<{ object: 'list'; data: PaymentSessionDto[] }>(
    `/connect/v1/payment-sessions?${params}`, { method: 'GET' }, authStage, getAccessToken,
  );
  return result.data;
}

export async function updatePaymentSessionStatusOnConnect(
  sessionId: string,
  status: 'processing' | 'incomplete',
  authStage: ConnectAuthStage,
  getAccessToken?: () => Promise<string | null>,
): Promise<void> {
  await connectRequest(
    `/connect/v1/payment-sessions/${sessionId}/actions/update-status`,
    { method: 'POST', body: { status } },
    authStage,
    getAccessToken,
  );
}

export async function getCompiledConfigFromConnect(
  target: { clientId?: string; operatorId?: string; accountId?: string },
  domain: 'theme' | 'content' | 'journey',
  authStage: ConnectAuthStage,
  getAccessToken?: () => Promise<string | null>,
): Promise<CompiledConfigDto | null> {
  const params = new URLSearchParams();
  if (target.clientId) params.set('client_id', target.clientId);
  if (target.operatorId) params.set('operator_id', target.operatorId);
  if (target.accountId) params.set('account_id', target.accountId);
  params.set('domain', domain);
  return connectRequest<CompiledConfigDto | null>(
    `/connect/v1/config/compiled?${params}`,
    { method: 'GET' },
    authStage,
    getAccessToken,
  );
}

export interface AuthSessionDto {
  account_id: string;
  customer_given_name: string | null;
  customer_family_name: string | null;
}

/** Stage-1 login readback: validates the access code and returns the account. */
export async function getAuthSessionFromConnect(accessCode: string): Promise<AuthSessionDto> {
  const response = await fetch(`${getConnectApiBaseUrl()}/connect/v1/auth/session`, {
    method: 'GET',
    headers: { 'Content-Type': 'application/json', 'X-Access-Code': accessCode },
  });
  if (!response.ok) {
    const errorData = await response.json().catch(() => null);
    const envelope = errorData as { error?: { message?: string } } | null;
    throw new ConnectApiError(envelope?.error?.message || `Connect API error: ${response.status}`, response.status, errorData);
  }
  return (await response.json()) as AuthSessionDto;
}

/** Boot theme/content before login — the only unauthenticated Connect call. */
export async function getPublicConfigFromConnect(
  target: { clientId?: string; operatorId?: string },
  domain: 'theme' | 'content',
): Promise<CompiledConfigDto | null> {
  const params = new URLSearchParams();
  if (target.clientId) params.set('client_id', target.clientId);
  if (target.operatorId) params.set('operator_id', target.operatorId);
  params.set('domain', domain);
  return connectRequest<CompiledConfigDto | null>(
    `/connect/v1/config/public?${params}`,
    { method: 'GET' },
    'anonymous',
  );
}

/** The account's shared Multibanco balance reference — one code for the whole
 *  outstanding balance, reused until it expires or the balance changes. */
export interface BalanceReferenceDto {
  entity: string;
  reference: string;
  amount_subunits: number;
  currency: string;
  expires_at: string | null;
}

export interface BalanceReferenceReadDto {
  object: 'balance_reference';
  reference: BalanceReferenceDto | null;
  outstanding_balance_subunits: number | null;
  /** Debt under an active payment plan is left out, so the amount is less than
   *  the customer's total balance. */
  excludes_arranged_cases: boolean;
}

export interface BalanceReferenceMintDto {
  object: 'balance_reference_mint';
  reference: BalanceReferenceDto | null;
  /** 'payoff' | 'no_active_balance' when nothing was minted. */
  reason: string | null;
}

/** Read-only: never mints. Throws ConnectApiError(404) when the customer has no
 *  account context or the feature is not enabled for their client. */
export async function getBalanceReferenceFromConnect(
  authStage: ConnectAuthStage,
  getAccessToken?: () => Promise<string | null>,
): Promise<BalanceReferenceReadDto> {
  return connectRequest<BalanceReferenceReadDto>(
    '/connect/v1/payments/balance-reference',
    { method: 'GET' },
    authStage,
    getAccessToken,
  );
}

/** Reuse-or-mint the account's code. Takes no amount — the server computes the
 *  outstanding balance, and the returned amount is what must be paid. */
export async function mintBalanceReferenceOnConnect(
  authStage: ConnectAuthStage,
  getAccessToken?: () => Promise<string | null>,
): Promise<BalanceReferenceMintDto> {
  return connectRequest<BalanceReferenceMintDto>(
    '/connect/v1/payments/balance-reference',
    { method: 'POST' },
    authStage,
    getAccessToken,
  );
}

export interface CaseReferenceReadDto {
  object: 'case_reference';
  reference: BalanceReferenceDto | null;
  /** Live custom-amount codes, already filtered server-side to those whose amount
   *  still fits the case balance — a code the balance has outgrown is not returned. */
  partial_references: BalanceReferenceDto[];
  /** The case's payable balance in subunits. `null` means the case is not payable
   *  at all (closed, a credit case, or covered by a plan) — which is NOT the same
   *  as a balance of zero, so never collapse the two. */
  payable_amount_subunits: number | null;
}

export interface CaseReferenceMintDto {
  object: 'case_reference_mint';
  reference: BalanceReferenceDto | null;
  /** Names the empty state when nothing was minted: 'payoff', 'not_payable',
   *  'exceeds_balance', 'past_handback', 'handback_too_soon',
   *  'channel_window_too_short', 'balance_changed'. */
  reason: string | null;
}

/** Read-only: never mints. Throws ConnectApiError(404) for a case the caller does
 *  not own, a case that does not exist, or a client the feature is off for — the
 *  three are deliberately indistinguishable. */
export async function getCaseReferenceFromConnect(
  caseId: string,
  authStage: ConnectAuthStage,
  getAccessToken?: () => Promise<string | null>,
): Promise<CaseReferenceReadDto> {
  return connectRequest<CaseReferenceReadDto>(
    `/connect/v1/payments/cases/${encodeURIComponent(caseId)}/balance-reference`,
    { method: 'GET' },
    authStage,
    getAccessToken,
  );
}

/** Reuse-or-mint a custom-amount code for this case. The amount is validated
 *  against the case balance server-side; asking for the whole balance is served
 *  the pay-in-full product instead, so the returned code may carry the longer
 *  window rather than the 24-hour one. */
export async function mintCasePartialReferenceOnConnect(
  caseId: string,
  amountSubunits: number,
  authStage: ConnectAuthStage,
  getAccessToken?: () => Promise<string | null>,
): Promise<CaseReferenceMintDto> {
  return connectRequest<CaseReferenceMintDto>(
    `/connect/v1/payments/cases/${encodeURIComponent(caseId)}/partial-reference`,
    { method: 'POST', body: { amount_subunits: amountSubunits } },
    authStage,
    getAccessToken,
  );
}

export interface ChatAttachmentDto {
  name: string;
  url: string;
  size: number;
  content_type: string | null;
  origin: string;
}

/** Uploads one AI-chat attachment. Multipart, so it bypasses connectRequest's
 *  JSON-only body handling and builds its own fetch call. */
export async function uploadChatAttachmentToConnect(
  file: File,
  accountId: string,
  authStage: ConnectAuthStage,
  getAccessToken?: () => Promise<string | null>,
): Promise<ChatAttachmentDto> {
  const headers = await getConnectApiHeaders(authStage, getAccessToken);
  delete headers['Content-Type'];

  const formData = new FormData();
  formData.append('file', file);
  formData.append('account_id', accountId);

  const response = await fetch(`${getConnectApiBaseUrl()}/connect/v1/attachments/actions/upload`, {
    method: 'POST',
    headers,
    body: formData,
  });

  if (!response.ok) {
    return throwConnectApiError(response);
  }

  return (await response.json()) as ChatAttachmentDto;
}

const BUDGET_ASSESSMENTS = '/connect/v1/budget-assessments';

/**
 * What the portal should do next, resolved server-side. Branch on this rather than
 * comparing `valid_until` — expiry has one authority and it is not the browser.
 */
export type BudgetAssessmentState = 'not_required' | 'none' | 'in_progress' | 'reusable' | 'refresh_required';

export type BudgetAssessmentTier = 'short' | 'full';
export type BudgetAnswerType = 'currency' | 'number' | 'single_select' | 'multi_select' | 'free_text';
export type BudgetAnswerCategory = 'income' | 'essential_expenditure' | 'priority_debt' | 'situation' | 'informational';

export interface BudgetAnswerLineDto {
  question_key: string;
  category: BudgetAnswerCategory;
  answer_type: BudgetAnswerType;
  answer_value: unknown;
  amount_cents: number | null;
}

/** Recomputed from the stored lines on every read, so it is never stale. Amounts are
 *  minor units of the assessment's own currency, and disposable income can be negative. */
export interface BudgetAffordabilitySummaryDto {
  currency: string;
  monthly_income_cents: number;
  monthly_essential_expenditure_cents: number;
  monthly_priority_debt_cents: number;
  monthly_outgoings_cents: number;
  monthly_disposable_income_cents: number;
}

export interface BudgetAssessmentDto {
  id: string;
  status: 'draft' | 'completed';
  tier: BudgetAssessmentTier;
  currency: string;
  disclosure_acknowledged_at: string | null;
  completed_at: string | null;
  valid_until: string | null;
  answers: BudgetAnswerLineDto[];
  summary: BudgetAffordabilitySummaryDto;
}

export interface CurrentBudgetAssessmentDto {
  state: BudgetAssessmentState;
  assessment: BudgetAssessmentDto | null;
}

/** `amount_cents` is required for and exclusive to `currency` questions — the server
 *  422s a currency answer without one and a non-currency answer that carries one. */
export interface BudgetAnswerInput {
  answer_value: unknown;
  amount_cents?: number;
}

export async function getCurrentBudgetAssessmentFromConnect(
  authStage: ConnectAuthStage,
  getAccessToken?: () => Promise<string | null>,
): Promise<CurrentBudgetAssessmentDto> {
  return connectRequest<CurrentBudgetAssessmentDto>(
    `${BUDGET_ASSESSMENTS}/current`,
    { method: 'GET' },
    authStage,
    getAccessToken,
  );
}

/** Idempotent: a second call returns the existing draft with its original tier. */
export async function startBudgetAssessmentOnConnect(
  tier: BudgetAssessmentTier,
  authStage: ConnectAuthStage,
  getAccessToken?: () => Promise<string | null>,
): Promise<BudgetAssessmentDto> {
  return connectRequest<BudgetAssessmentDto>(
    `${BUDGET_ASSESSMENTS}/actions/start`,
    { method: 'POST', body: { tier } },
    authStage,
    getAccessToken,
  );
}

/** The hard gate: answers and completion both 404 until this has been called. */
export async function acknowledgeBudgetDisclosureOnConnect(
  assessmentId: string,
  authStage: ConnectAuthStage,
  getAccessToken?: () => Promise<string | null>,
): Promise<BudgetAssessmentDto> {
  return connectRequest<BudgetAssessmentDto>(
    `${BUDGET_ASSESSMENTS}/${assessmentId}/actions/acknowledge-disclosure`,
    { method: 'POST' },
    authStage,
    getAccessToken,
  );
}

/** Upserts on the question key, so editing an earlier answer replaces its line rather
 *  than appending. Returns the whole assessment with the summary recomputed. */
export async function saveBudgetAnswerOnConnect(
  assessmentId: string,
  questionKey: string,
  answer: BudgetAnswerInput,
  authStage: ConnectAuthStage,
  getAccessToken?: () => Promise<string | null>,
): Promise<BudgetAssessmentDto> {
  return connectRequest<BudgetAssessmentDto>(
    `${BUDGET_ASSESSMENTS}/${assessmentId}/answers/${encodeURIComponent(questionKey)}`,
    { method: 'PUT', body: answer },
    authStage,
    getAccessToken,
  );
}

export async function completeBudgetAssessmentOnConnect(
  assessmentId: string,
  authStage: ConnectAuthStage,
  getAccessToken?: () => Promise<string | null>,
): Promise<BudgetAssessmentDto> {
  return connectRequest<BudgetAssessmentDto>(
    `${BUDGET_ASSESSMENTS}/${assessmentId}/actions/complete`,
    { method: 'POST' },
    authStage,
    getAccessToken,
  );
}

const PAYMENT_OFFERS = '/connect/v1/payment-offers';

export type OfferFrequency = 'weekly' | 'biweekly' | 'monthly';

/**
 * The whole of what the customer is told, and deliberately coarser than the decision reason
 * the server stores: a flagged situation and an agent-recorded vulnerability mark both land
 * on `contact_us`, and naming either back would disclose what we hold about someone from a
 * screen they reached by offering to pay.
 */
export type OfferOutcome = 'accepted' | 'pending_review' | 'contact_us';

/**
 * The terms only. The balance the offer is measured against, the currency, the assessment
 * behind the decision and the thresholds are all resolved server-side — a client that could
 * name its own balance could clear a length threshold by understating what it owes.
 *
 * `case_id: null` means the offer covers the whole account.
 */
export interface SubmitOfferRequest {
  case_id: string | null;
  instalment_amount_cents: number;
  frequency: OfferFrequency;
  instalment_count: number;
}

/** The frozen terms off the recorded row, not the ones posted: the monthly equivalent and the
 *  plan length are derived server-side and the balance is the server's own figure. */
export interface PaymentOfferDto {
  id: string;
  case_id: string | null;
  currency: string;
  instalment_amount_cents: number;
  frequency: OfferFrequency;
  instalment_count: number;
  plan_length_months: number;
  monthly_equivalent_cents: number;
  balance_cents: number;
  status: 'pending' | 'accepted' | 'rejected';
  created_at: string;
}

/**
 * - `decided` — the offer is on the record; `outcome` says what to show.
 * - `assessment_required` — nothing was written; collect an assessment of `required_tier`
 *   first, then post the same offer again.
 *
 * `required_tier` is why that second state carries a body at all: the thresholds that decide
 * the depth are withheld from the customer config projection, and `budget-assessments/actions/start`
 * takes the tier as an unverified input, so a portal guessing `short` asks 9 questions where
 * the plan needed 19.
 */
export interface SubmitOfferResponseDto {
  state: 'decided' | 'assessment_required';
  outcome?: OfferOutcome | null;
  offer?: PaymentOfferDto | null;
  required_tier?: BudgetAssessmentTier | null;
}

export async function submitPaymentOfferOnConnect(
  body: SubmitOfferRequest,
  authStage: ConnectAuthStage,
  getAccessToken?: () => Promise<string | null>,
): Promise<SubmitOfferResponseDto> {
  return connectRequest<SubmitOfferResponseDto>(PAYMENT_OFFERS, { method: 'POST', body }, authStage, getAccessToken);
}

/**
 * What the response means, with no policy applied.
 *
 * There is no `checkout` branch on purpose: whether an `accepted` verdict goes straight to
 * checkout or pauses on a confirmation is a product decision that depends on how the customer
 * arrived, so it belongs to `useOfferGate` rather than to this reading of the payload.
 */
const KNOWN_OUTCOMES: readonly OfferOutcome[] = ['accepted', 'pending_review', 'contact_us'];

export type OfferNext =
  | { next: 'assessment'; tier: BudgetAssessmentTier }
  | { next: 'outcome'; outcome: OfferOutcome; offer: PaymentOfferDto | null };

export function nextAfterOffer(response: SubmitOfferResponseDto): OfferNext {
  if (response.state === 'assessment_required' && response.required_tier) {
    return { next: 'assessment', tier: response.required_tier };
  }
  // Anything malformed falls to `contact_us` rather than to the permissive branch: a response
  // nobody has decided how to phrase must not read to the customer as "you're all set". The
  // outcome is checked against the set this app can actually render, not merely for presence —
  // the two services deploy independently, and an outcome added on one side would otherwise
  // reach `OUTCOMES[outcome]` as undefined and take the customer to the error boundary instead
  // of to a human.
  const outcome =
    response.state === 'decided' && KNOWN_OUTCOMES.includes(response.outcome as OfferOutcome)
      ? (response.outcome as OfferOutcome)
      : 'contact_us';
  return { next: 'outcome', outcome, offer: response.offer ?? null };
}
